/**
 * categorizerDisclosureStore (ARC-15)
 *
 * Read/write side of the categorizer disclosure acknowledgement. Storage is
 * server-side on the user record — never localStorage — so the acknowledgement
 * survives a device change, a cache wipe, and a sign-out/sign-in cycle.
 *
 * Stored fields (per ARC-15):
 *   categorizerDisclosureAcceptedVersion  string     e.g. "1.0"
 *   categorizerDisclosureAcceptedAt       ISO string e.g. "2026-08-04T…Z"
 *
 * The Firestore call itself lives in services/disclosureService.js, which is
 * the only layer in this app allowed to touch user records. That file is
 * human-only, so the two functions used here ship as
 * patches/disclosureService-categorizer.patch. Until that patch is applied the
 * calls below are absent: reads fail closed (the gate shows) and a write
 * surfaces a retryable error instead of silently claiming success.
 */

import * as disclosureService from "../../services/disclosureService";

const SAVE_UNAVAILABLE =
  "Couldn't save your acknowledgement. Please check your connection and try again.";

/* The two categorizer functions ship in the patch, so they are absent from the
   module until a human applies it. Reading them off a copy of the namespace
   resolves them at call time — a direct `disclosureService.fetchCategorizer…`
   is a link-time reference and would fail the production build pre-patch. */
const svc = () => ({ ...disclosureService });

export const getCurrentUid = () => {
  try {
    return disclosureService.getCurrentUid?.() || null;
  } catch (_) {
    return null;
  }
};

/**
 * The member's stored acknowledgement, or null when there is none.
 * Returns { version, acceptedAt }. Any failure resolves to null — a member we
 * cannot confirm has acknowledged is a member who sees the disclosure.
 */
export async function loadCategorizerDisclosure(uid = getCurrentUid()) {
  if (!uid) return null;
  const fetchAck = svc().fetchCategorizerDisclosureAck;
  if (typeof fetchAck !== "function") return null;
  try {
    const record = await fetchAck(uid);
    const version = record?.categorizerDisclosureAcceptedVersion;
    if (!version) return null;
    return {
      version: String(version),
      acceptedAt: record?.categorizerDisclosureAcceptedAt || null,
    };
  } catch (_) {
    return null;
  }
}

/**
 * Persist an acknowledgement of `version`. Resolves to the stored record;
 * rejects (with a member-readable message) if it could not be written, so the
 * caller can keep the modal open rather than pretend it landed.
 */
export async function saveCategorizerDisclosure(version, uid = getCurrentUid()) {
  if (!uid) {
    throw new Error(
      "You need to be signed in to continue. Please refresh and try again."
    );
  }
  const writeAck = svc().writeCategorizerDisclosureAck;
  if (typeof writeAck !== "function") throw new Error(SAVE_UNAVAILABLE);
  const record = await writeAck(uid, version);
  return {
    version: String(record?.categorizerDisclosureAcceptedVersion || version),
    acceptedAt: record?.categorizerDisclosureAcceptedAt || null,
  };
}

/** True when `record` is an acknowledgement of exactly the current version. */
export const isAcknowledgementCurrent = (record, currentVersion) =>
  !!record && !!currentVersion && record.version === currentVersion;
